import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, getCurrentInstance, defineAsyncComponent, nextTick, watch } from 'vue'
import { Button, DatePickerRangeInline, DatePicker, Backdrop } from 'ui-shared-components';
import { DATE_TIME_FORMATS, formatWithLocale } from '@white-label-helper/date-utilities';


export default /*#__PURE__*/_defineComponent({
  __name: 'date-range-picker',
  props: {
    inlineDatePicker: { type: Boolean, default: false },
    startDateLabel: { default: '' },
    endDateLabel: { default: '' },
    presetStartDate: { default: '' },
    presetEndDate: { default: '' },
    maxDateYearsFromSelectedStartDate: { default: 0 },
    errors: { default: () => ({
      startDate: '',
      endDate: '',
    }) }
  },
  emits: ['open', 'close', 'input', 'input-start-date', 'input-end-date'],
  setup(__props: any, { emit }) {

const props = __props as { inlineDatePicker: boolean, startDateLabel: string, endDateLabel: string, presetStartDate: Date | string, presetEndDate: Date | string, maxDateYearsFromSelectedStartDate: number, errors: {
      startDate: string;
      endDate: string;
    } };

const IconCalendar = defineAsyncComponent(() => import('@white-label-icon/icon-calendar'));
const IconError = defineAsyncComponent(() => import('@white-label-icon/icon-error'));

// Current value of the dates
const form = ref({
  startDate: '' as Date | string,
  endDate: '' as Date | string,
})

// To store a copy of the dates for restoring later if needed
const previousDates = ref({
  startDate: form.value.startDate,
  endDate: form.value.endDate,
});

const datePickerOpened = ref(false);
const proxy = getCurrentInstance()?.proxy as Vue;



const useFixedBottomCalendar = computed(() => {
  return proxy.isMobileResolution || proxy.isTabletResolution;
})

const showInputs = computed(() => {
  if (props.inlineDatePicker) return !datePickerOpened.value;
  return true;
});

watch(
  [() => props.presetStartDate, () => props.presetEndDate],
  ([newStart, newEnd]) => {
    if (newStart) {
      form.value.startDate = new Date(newStart);
      emit('input-start-date', form.value.startDate);
    }
    if (newEnd) {
      form.value.endDate = new Date(newEnd);
      emit('input-end-date', form.value.endDate);
    }
  },
  { immediate: true },
)

const computedStartDateLabel = computed(() => props.startDateLabel || proxy.$t('UI.dateRangeInput.label.startDate'));
const computedEndDateLabel = computed(() => props.endDateLabel || proxy.$t('UI.dateRangeInput.label.endDate'));

function formatDate(date: Date | string): Date | string {
  let dateConverted: Date | string = date;
  if (date && !(date instanceof Date)) {
    dateConverted = new Date(date);
  }
  return dateConverted && dateConverted instanceof Date
    ? formatWithLocale(dateConverted, DATE_TIME_FORMATS.weekday_day_month_year, proxy.$i18n.locale)
    : '';
}

const openDatePicker = async (input: 'start' | 'end') => {
  // Copy current date values to restore later if needed
  previousDates.value.startDate = form.value.startDate;
  previousDates.value.endDate = form.value.endDate;

  // If opening from the start date input, clear both dates
  if (props.inlineDatePicker) {
    if (input === 'start') {
      form.value.startDate = '';
      form.value.endDate = '';
    } else {
      // If opening from the end date input, clear only the end date
      form.value.endDate = '';
    }
  }

  datePickerOpened.value = true;
  emit('open');

  // Wait for the DOM update, then focus on the flatpickr element
  await nextTick();
  const datepickerRangeInline = proxy.$refs['datepicker-range-inline'] as DatePickerRangeInline;
  if (datepickerRangeInline) {
    datepickerRangeInline.$refs['flatpickr-element']?.$el?.focus();
  }
};

const closeDatePicker = async () => {
  // If datepicker was closed without saving, restore the original dates
  if (props.inlineDatePicker) {
    if (!form.value.startDate && previousDates.value.startDate) {
      form.value.startDate = previousDates.value.startDate;
    }
    if (!form.value.endDate && previousDates.value.endDate) {
      form.value.endDate = previousDates.value.endDate;
    }
    //emits restored dates back to form
    emit('input', { startDate: form.value.startDate, endDate: form.value.endDate });
  }

  datePickerOpened.value = false;
  await nextTick();
  emit('close');

  // If no start date, focus on the start date button
  if (!form.value.startDate) {
    const startDateButton = proxy.$el.querySelector('#date-time-picker__start-date') as HTMLButtonElement;
    startDateButton.focus();
  } else {
    // Otherwise focus on the end date button
    const endDateButton = proxy.$el.querySelector('#date-time-picker__end-date') as HTMLButtonElement;
    if (endDateButton) {
      endDateButton.focus();
    }
  }
};

const onChangeDate = (dates: (Date | string)[]) => {
  const [date1, date2] = dates.map((date) => {
    if (date && !(date instanceof Date)) {
      return new Date(date);
    }
    return date;
  });

  form.value.startDate = date1 ?? '';
  form.value.endDate = date2 ?? '';

  if (form.value.startDate && form.value.endDate &&
    new Date(form.value.startDate) > new Date(form.value.endDate)) {
    form.value.endDate = '';
  } else if (form.value.startDate && form.value.endDate)  {
    // If both dates are selected, emit the input event with the new dates
    emit('input', { startDate: form.value.startDate, endDate: form.value.endDate });
  }

  if (!props.inlineDatePicker && form.value.startDate && form.value.endDate) {
    // If both dates are selected, close the date picker
    closeDatePicker();
  }
};

const headerLabel = computed(() => {
  if (!form.value.startDate) return proxy.$t('UI.dateRangeInput.placeholder.startDate');
  if (!form.value.endDate) return proxy.$t('UI.dateRangeInput.placeholder.endDate');
  return proxy.$t('UI.dateRangeInput.yourTripDates');
});

const minDate = computed(() => new Date());
const maxDate = computed(() => {
  if (props.maxDateYearsFromSelectedStartDate) {
    // sets max date in relation to the start date from prop
    const baseDate = form.value.startDate ? new Date(form.value.startDate) : new Date()
    const yearsToAdd = props.maxDateYearsFromSelectedStartDate - 1;

    // if maxDateYearsFromSelectedStartDate is set to more than 1 year, it will add additional full year(s) + 364days
    if (yearsToAdd > 0) {
      baseDate.setFullYear(baseDate.getFullYear() + yearsToAdd);
    }

    // 364 days - which is one year if we include starting day
    baseDate.setDate(baseDate.getDate() + 364);
    return baseDate;
  }
  // use default max date of 10 years from today
  return new Date(new Date().setFullYear(new Date().getFullYear() + 10));
});



return { __sfc: true,IconCalendar, IconError, form, previousDates, datePickerOpened, proxy, props, useFixedBottomCalendar, showInputs, computedStartDateLabel, computedEndDateLabel, formatDate, openDatePicker, closeDatePicker, onChangeDate, headerLabel, minDate, maxDate, emit, Button, DatePickerRangeInline, DatePicker, Backdrop }
}

})